import React from "react";

/* ---------- TABLES ---------- */
const TABLE_SEP_RE = /^\s*\|?\s*(?:\:?-{3,}:?\s*\|)+\s*\:?-{3,}:?\s*\|?\s*$/;

const splitCells = (line) => {
  const cleaned = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  return cleaned.split("|").map((s) => s.trim());
};

/* ---------- INLINE (bold/italic/strike/code/links/images/autolink) ---------- */
function renderInline(text, keyPrefix = "") {
  if (text == null || text === "") return [text];

  // Порядок важливий
  const patterns = [
    // ![alt](src "title")
    {
      re: /!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]+)")?\)/g,
      render: (m, i) => (
        <img
          key={`${keyPrefix}img-${i}`}
          src={m[2]}
          alt={m[1]}
          title={m[3] || ""}
          style={{ maxWidth: "100%" }}
        />
      ),
    },
    // [text](href "title")
    {
      re: /\[([^\]]+)\]\(([^)\s]+)(?:\s+"([^"]+)")?\)/g,
      render: (m, i) => (
        <a
          key={`${keyPrefix}a-${i}`}
          href={m[2]}
          title={m[3] || ""}
          target="_blank"
          rel="noopener noreferrer"
        >
          {m[1]}
        </a>
      ),
    },
    // `code`
    {
      re: /`([^`]+)`/g,
      render: (m, i) => (
        <code
          key={`${keyPrefix}code-${i}`}
          style={{
            background: "rgba(0,0,0,0.06)",
            padding: "0 4px",
            borderRadius: 4,
          }}
        >
          {m[1]}
        </code>
      ),
    },
    // **bold** or __bold__
    {
      re: /(\*\*|__)([^]+?)\1/g,
      render: (m, i) => (
        <strong key={`${keyPrefix}b-${i}`}>
          {renderInline(m[2], `${keyPrefix}b-${i}-`)}
        </strong>
      ),
    },
    // *italic* or _italic_
    {
      re: /(\*|_)([^]+?)\1/g,
      render: (m, i) => (
        <em key={`${keyPrefix}i-${i}`}>
          {renderInline(m[2], `${keyPrefix}i-${i}-`)}
        </em>
      ),
    },
    // ~~strike~~
    {
      re: /~~([^]+?)~~/g,
      render: (m, i) => (
        <del key={`${keyPrefix}s-${i}`}>
          {renderInline(m[1], `${keyPrefix}s-${i}-`)}
        </del>
      ),
    },
    // автолінк http/https
    {
      re: /(https?:\/\/[^\s<]+[^<.,:;)"\]\s])/g,
      render: (m, i) => (
        <a
          key={`${keyPrefix}autol-${i}`}
          href={m[1]}
          target="_blank"
          rel="noopener noreferrer"
        >
          {m[1]}
        </a>
      ),
    },
  ];

  const out = [];
  let remaining = text;
  let guard = 0;

  while (remaining && guard++ < 10000) {
    let firstHit = null;

    for (let pIdx = 0; pIdx < patterns.length; pIdx++) {
      const re = new RegExp(patterns[pIdx].re);
      const m = re.exec(remaining);
      if (m && (!firstHit || m.index < firstHit.idx)) {
        firstHit = { idx: m.index, m, pIdx };
      }
    }

    if (!firstHit) {
      out.push(remaining);
      break;
    }
    if (firstHit.idx > 0) {
      out.push(remaining.slice(0, firstHit.idx));
    }
    out.push(patterns[firstHit.pIdx].render(firstHit.m, out.length));
    remaining = remaining.slice(
      firstHit.idx + firstHit.m[0].length
    );
  }
  return out;
}

/* ---------- Блоковий парсер ---------- */
function parseBlocks(src) {
  const lines = String(src || "").replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // пусті рядки
    if (/^\s*$/.test(line)) {
      i++;
      continue;
    }

    // HR
    if (/^(\*\s*\*\s*\*|-{3,}|_{3,})\s*$/.test(line)) {
      blocks.push({ type: "hr" });
      i++;
      continue;
    }

    // Headings
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      blocks.push({ type: "heading", level: h[1].length, text: h[2].trim() });
      i++;
      continue;
    }

    // Blockquote
    if (/^>\s?/.test(line)) {
      const q = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        q.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ type: "blockquote", lines: q });
      continue;
    }

    // Fenced code block ```lang
    const fence = /^```([\w-]+)?\s*$/.exec(line);
    if (fence) {
      const lang = fence[1] || null;
      i++;
      const code = [];
      while (i < lines.length && !/^```/.test(lines[i])) {
        code.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++; // закриваючі ```
      blocks.push({ type: "code", lang, lines: code });
      continue;
    }

    // TABLE
    if (line.includes("|") && TABLE_SEP_RE.test(lines[i + 1] || "")) {
      const start = i;
      let j = i + 2;
      while (
        j < lines.length &&
        lines[j].includes("|") &&
        !/^```/.test(lines[j]) &&
        !/^\s*$/.test(lines[j])
      ) {
        j++;
      }
      const block = lines.slice(start, j);
      const headers = splitCells(block[0]);
      const rows = block.slice(2).map(splitCells);
      blocks.push({ type: "table", headers, rows });
      i = j;
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      const firstMatch = /^\s*(\d+)\.\s+/.exec(line);
      const startNum = firstMatch ? parseInt(firstMatch[1], 10) : 1;

      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i++;
        // дозволяємо пусті рядки між пунктами
        while (i < lines.length && /^\s*$/.test(lines[i])) i++;
      }
      blocks.push({ type: "olist", start: startNum, items });
      continue;
    }

    // Unordered list
    if (/^\s*[-*+]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*+]\s+/, ""));
        i++;
        // дозволяємо пусті рядки між пунктами
        while (i < lines.length && /^\s*$/.test(lines[i])) i++;
      }
      blocks.push({ type: "ulist", items });
      continue;
    }

    // Paragraph (до порожнього або іншого блоку)
    const p = [];
    while (
      i < lines.length &&
      !/^\s*$/.test(lines[i]) &&
      !/^(\*\s*\*\s*\*|-{3,}|_{3,})\s*$/.test(lines[i]) &&
      !/^(#{1,6})\s+/.test(lines[i]) &&
      !/^>\s?/.test(lines[i]) &&
      !/^```/.test(lines[i]) &&
      !(lines[i].includes("|") && TABLE_SEP_RE.test(lines[i + 1] || "")) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !/^\s*[-*+]\s+/.test(lines[i])
    ) {
      p.push(lines[i]);
      i++;
    }
    if (p.length) blocks.push({ type: "paragraph", lines: p });
  }

  return blocks;
}

/* ---------- React-рендер ---------- */
function MarkdownTable({ headers, rows }) {
  return (
    <div className="md-table-wrap" style={{ overflowX: "auto" }}>
      <table className="md-table">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i}>{renderInline(h, `th-${i}-`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j}>{renderInline(c, `td-${i}-${j}-`)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MarkdownRenderer({ text }) {
  const blocks = parseBlocks(text);

  return (
    <div className="markdown-root">
      {blocks.map((b, i) => {
        switch (b.type) {
          case "heading": {
            const lvl = Math.min(6, Math.max(1, b.level));
            const Tag = `h${lvl}`;
            return React.createElement(
              Tag,
              { key: i },
              renderInline(b.text, `h-${i}-`)
            );
          }
          case "hr":
            return <hr key={i} />;
          case "blockquote":
            return (
              <blockquote key={i}>
                {b.lines.map((ln, k) => (
                  <p key={k} style={{ whiteSpace: "pre-wrap" }}>
                    {renderInline(ln, `q-${i}-${k}-`)}
                  </p>
                ))}
              </blockquote>
            );
          case "olist":
            return (
              <ol key={i} start={b.start || 1}>
                {b.items.map((it, k) => (
                  <li key={k}>{renderListItem(it, `ol-${i}-${k}-`)}</li>
                ))}
              </ol>
            );
          case "ulist":
            return (
              <ul key={i}>
                {b.items.map((it, k) => (
                  <li key={k}>{renderListItem(it, `ul-${i}-${k}-`)}</li>
                ))}
              </ul>
            );
          case "code":
            return (
              <pre key={i}>
                <code className={b.lang ? `language-${b.lang}` : undefined}>
                  {b.lines.join("\n")}
                </code>
              </pre>
            );
          case "table":
            return <MarkdownTable key={i} headers={b.headers} rows={b.rows} />;
          case "paragraph":
            return (
              <p key={i} style={{ whiteSpace: "pre-wrap" }}>
                {renderInline(b.lines.join("\n"), `p-${i}-`)}
              </p>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

/* чекбокси у списках: [ ] / [x] */
function renderListItem(text, keyPrefix) {
  const m = /^\[([ xX])\]\s+(.+)$/.exec(text);
  if (m) {
    const checked = m[1].toLowerCase() === "x";
    return (
      <>
        <input type="checkbox" checked={checked} readOnly />{" "}
        {renderInline(m[2], keyPrefix)}
      </>
    );
  }
  return renderInline(text, keyPrefix);
}
